import { IDisposable } from '@sm/max-common'

export class Events implements IDisposable {
  private send: undefined | ((type: string, params?: any) => void) = undefined
  private registerAppEvent?: (key: string, fn: Function) => void
  private unregisterAppEvent?: (key: string, fn: Function) => void = undefined
  private onDispose?: () => void = undefined

  private cache = new Map<string, Set<Function>>()
  private fnCache = new Map<string, Set<Map<Function, Function>>>()

  constructor(config?: {
    emit?(evtType: string, params: any): void
    registerAppEvent?(key: string, fn?: Function): void
    unregisterAppEvent?(key: string): void
    onDispose?: () => void
  }) {
    this.send = config?.emit
    this.registerAppEvent = config?.registerAppEvent
    this.unregisterAppEvent = config?.unregisterAppEvent
    this.onDispose = config?.onDispose
  }

  on = <T>(
    key: string,
    fn: (params: T) => void,
  ) => {
    if (!key) throw new Error('The parameter "key" cannot be empty')

    const fns = this.cache.get(key) || new Set()
    const onFn = (params?: string) => {
      fn(params && JSON.parse(params))
    }
    fns.add(onFn)
    this.cache.set(key, fns)
    const fnMap = new Map().set(fn, onFn)
    this.fnCache.set(key, (this.fnCache.get(key) ?? new Set()).add(fnMap))
    return this.registerAppEvent(key, onFn)
  }

  once = <T>(
    key: string,
    fn: (params: T) => void,
  ) => {
    if (!key) throw new Error('The parameter "key" cannot be empty')

    const fns = this.cache.get(key) || new Set()
    const onceFn = (params?: string) => {
      fn(params && JSON.parse(params))
      this.unregisterAppEvent(key, onceFn)
      fns.delete(onceFn)
    }
    fns.add(onceFn)
    this.cache.set(key, fns)
    return this.registerAppEvent(key, onceFn)
  }

  off = (
    key: string,
    fn?: Function,
  ) => {
    if (!key) throw new Error('The parameter "key" cannot be empty')

    const fnSet = this.cache.get(key)
    if (!fnSet) return
    if (fn) {
      const mapSet = this.fnCache.get(key) || new Set()
      let onFn
      let _map
      for (const map of mapSet) {
        if (map.get(fn)) {
          _map = map
          onFn = map.get(fn)
          break
        }
      }
      if (onFn) {
        fnSet.delete(onFn)
        this.unregisterAppEvent(key, onFn)
        mapSet.delete(_map)
      }
    } else {
      Array.from(fnSet.values()).forEach((fn) => {
        if (fn) this.unregisterAppEvent(key, fn)
      })
      this.cache.delete(key)
    }
  }

  emit = <T>(key: string, params?: T) => {
    if (Number.isNaN(params)) {
      throw Error('The parameter "params" is "NaN"')
    }
    if (!key) throw new Error('The parameter "key" cannot be empty')

    const _param = params ?? {}
    this.send(key, JSON.stringify(_param))
  }

  dispose(): void {
    Array.from(this.cache.keys()).forEach(k => this.off(k))
    this.onDispose && this.onDispose()
  }
}
